import Vue from 'vue';
import Vuex from 'vuex';

import DialogueApi from '@/api/DialogueApi';
import ShopApi from '@/api/ShopApi';
import offerStore from '@/store/offerStore';
import { isInStock } from '@/utils';

Vue.use(Vuex);

// The offer endpoints all expect chosen quantities promoted onto `quantity`.
const buildOfferPayload = (state) => {
  const categoryProducts = state.offerStore.productsPerCategory;
  Object.keys(categoryProducts).forEach((category) => {
    categoryProducts[category].offeredProducts.forEach((offeredProduct) => {
      offeredProduct.quantity = offeredProduct.chosenQuantity;
      offeredProduct.availableProducts[
        offeredProduct.selectedProductIndex
      ].upsaleProducts.forEach(
        (accessory) => (accessory.quantity = accessory.chosenQuantity),
      );
    });
  });

  return {
    productsPerCategory: categoryProducts,
    totalPrice: state.offerStore.totalPrice,
    answeredQuestions: state.answeredQuestionsV2,
    legalCustomer: state.customerType === 'legal',
  };
};

export default new Vuex.Store({
  modules: {
    offerStore,
  },
  state: {
    allAnswers: {},
    answers: {},
    answeredQuestions: [],
    answeredQuestionsV2: [],
    pendingQuestion: null,
    complete: false,
    isDownloading: false,
    isGetRedirectUrlLoading: false,
    isShopRedirectError: false,
    redirectUrl: null,
    offerShareUrl: null,
    isOfferShareUrlLoading: false,
    customerType: null,
    leadDetails: null,
    isQuoteRequestLoading: false,
    quoteRequestError: false,
    quoteRequestSent: false,
  },
  getters: {
    questions(state) {
      const questions = [...state.answeredQuestions];
      const alreadyAnswered = questions.find(
        (question) => question.id === state.pendingQuestion?.id,
      );

      if (state.pendingQuestion !== null && !alreadyAnswered) {
        questions.push({ ...state.pendingQuestion, next: true });
      }

      return questions;
    },
  },
  actions: {
    async startDialogue({ commit }) {
      commit('resetInstance');
      const dialogue = await DialogueApi.start();
      commit('setPendingQuestion', dialogue.pendingQuestion);
    },
    async evaluateAnswer({ commit, state }, { answerVariables, question }) {
      commit('setAnsweredQuestions', question);
      commit('setAllAnswers', answerVariables);
      commit('setAnswers', answerVariables);

      const dialogue = await DialogueApi.evaluate(state.answers);

      commit('setAnsweredQuestionsV2', dialogue.answeredQuestions);
      commit('setPendingQuestion', dialogue.pendingQuestion);
      commit('setComplete', dialogue.complete);

      if (dialogue.offer) {
        commit('offerStore/setOffer', { offer: dialogue.offer, customerType: state.customerType });
      }
    },

    async downloadPdf({ state, commit }) {
      commit('setDownloading', true);
      try {
        const responseData = await DialogueApi.downloadPdf(
          buildOfferPayload(state),
        );
        const url = window.URL.createObjectURL(new Blob([responseData]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'offer.pdf');
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        console.error(error);
      } finally {
        commit('setDownloading', false);
      }
    },
    async getRedirectUrl({ commit, rootGetters }) {
      commit('setToShopLoading', true);
      commit('setShopRedirectError', false);
      const products = {
        FenceProducts: rootGetters['offerStore/getSelectedProducts']
          .filter((product) => isInStock(product, product.packsNeeded))
          .map((product) => ({
            productNumber: product.artNo,
            quantity: product.packsNeeded,
          })),
      };
      try {
        const response = await ShopApi.proceedToShop(products);
        commit('setRedirectUrl', response.redirectUrl);
      } catch (err) {
        commit('setShopRedirectError', true);
      } finally {
        commit('setToShopLoading', false);
      }
    },
    async getOfferShareUrl({ state, commit }) {
      commit('setOfferShareUrlLoading', true);
      try {
        const data = await DialogueApi.shareOffer(buildOfferPayload(state));
        commit('setOfferShareUrl', `${globalThis.location.origin}/api/offer/pdf/${data.token}`);
      } catch (error) {
        console.error(error);
      } finally {
        commit('setOfferShareUrlLoading', false);
      }
    },
    async sendQuoteRequest({ state, commit }, leadDetails) {
      commit('setQuoteRequestLoading', true);
      commit('setQuoteRequestError', false);
      commit('setLeadDetails', leadDetails);
      try {
        const data = await DialogueApi.requestQuote({
          ...buildOfferPayload(state),
          quoteRequest: true,
          leadDetails,
        });
        commit(
          'setOfferShareUrl',
          `${globalThis.location.origin}/api/offer/pdf/${data.token}`,
        );
        commit('setQuoteRequestSent', true);
        return true;
      } catch (error) {
        console.error(error);
        commit('setQuoteRequestError', true);
        return false;
      } finally {
        commit('setQuoteRequestLoading', false);
      }
    },
  },
  mutations: {
    setLeadDetails(state, leadDetails) {
      state.leadDetails = leadDetails;
    },
    setQuoteRequestLoading(state, isLoading) {
      state.isQuoteRequestLoading = isLoading;
    },
    setQuoteRequestError(state, hasError) {
      state.quoteRequestError = hasError;
    },
    setQuoteRequestSent(state, isSent) {
      state.quoteRequestSent = isSent;
    },
    setAllAnswers(state, newAnswers) {
      state.allAnswers = { ...state.allAnswers, ...newAnswers };
    },
    setAnswers(state, newAnswers) {
      let answers = { ...state.answers };
      const stateAnswersKeys = Object.keys(answers);
      const isAnswerChange = Object.keys(newAnswers).every((answer) =>
        stateAnswersKeys.includes(answer),
      );

      if (isAnswerChange) {
        const answeredAnswersKeys = state.answeredQuestions
          .map(({ decisions }) =>
            decisions
              .filter(({ key }) => stateAnswersKeys.includes(key))
              .map(({ key }) => key),
          )
          .flat();

        answers = Object.fromEntries(
          Object.entries(answers).filter(([key]) =>
            answeredAnswersKeys.includes(key),
          ),
        );
      }

      state.answers = { ...answers, ...newAnswers };
    },
    setAnsweredQuestions(state, question) {
      const alreadyAnsweredIndex = state.answeredQuestions.findIndex(
        (q) => q.id === question.id,
      );

      if (alreadyAnsweredIndex < 0) {
        if (state.pendingQuestion) {
          state.answeredQuestions = [
            ...state.answeredQuestions,
            state.pendingQuestion,
          ];
        }
      } else {
        state.answeredQuestions = state.answeredQuestions.splice(
          0,
          alreadyAnsweredIndex + 1,
        );
      }
    },
    setAnsweredQuestionsV2(state, question) {
      state.answeredQuestionsV2 = question;
    },
    setPendingQuestion(state, pendingQuestion) {
      state.pendingQuestion = pendingQuestion;
    },
    setComplete(state, complete) {
      state.complete = complete;
    },
    setDownloading(state, isDownloading) {
      state.isDownloading = isDownloading;
    },
    resetInstance(state) {
      state.allAnswers = {};
      state.answers = {};
      state.answeredQuestions = [];
      state.answeredQuestionsV2 = [];
      state.pendingQuestion = null;
      state.complete = false;
      state.customerType = null;
      state.offerShareUrl = null;
      state.isOfferShareUrlLoading = false;
      state.leadDetails = null;
      state.isQuoteRequestLoading = false;
      state.quoteRequestError = false;
      state.quoteRequestSent = false;

      state.offerStore.productsPerCategory = null;
      state.offerStore.totalPrice = null;
    },
    setCustomerType(state, customerType) {
      state.customerType = customerType;
    },
    setToShopLoading(state, isLoading) {
      state.isGetRedirectUrlLoading = isLoading;
    },
    setShopRedirectError(state, isError) {
      state.isShopRedirectError = isError;
    },
    setRedirectUrl(state, newUrl) {
      state.redirectUrl = newUrl;
    },
    setOfferShareUrl(state, url) {
      state.offerShareUrl = url;
    },
    setOfferShareUrlLoading(state, isLoading) {
      state.isOfferShareUrlLoading = isLoading;
    },
  },
});
